import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { PHONE } from "@/mocks/updiag";
import { SHOW_REVIEWS } from "@/config/features";

type NavLink = {
  label: string;
  href: string;
  dropdown?: { label: string; href: string }[];
};

const allNavLinks: NavLink[] = [
  { label: "Accueil", href: "/" },
  {
    label: "Diagnostics",
    href: "/diagnostics",
    dropdown: [
      { label: "★ DPE — Service phare", href: "/diagnostics/dpe" },
      { label: "Pack Vente (DDT)", href: "/diagnostics/pack-vente" },
      { label: "Pack Location", href: "/diagnostics/pack-location" },
      { label: "Amiante", href: "/diagnostics/amiante" },
      { label: "Plomb (CREP)", href: "/diagnostics/plomb" },
      { label: "Électricité", href: "/diagnostics/electricite" },
      { label: "Gaz", href: "/diagnostics/gaz" },
      { label: "Termites", href: "/diagnostics/termites" },
      { label: "Loi Carrez", href: "/diagnostics/loi-carrez" },
    ],
  },
  { label: "Tarifs", href: "/tarifs" },
  { label: "Zones", href: "/zones" },
  { label: "Avis", href: "/avis" },
  { label: "Blog", href: "/blog" },
];
const navLinks: NavLink[] = allNavLinks.filter((l) => SHOW_REVIEWS || l.href !== "/avis");

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  // Ouverture immédiate (on annule un timer de fermeture en cours si présent)
  const openDropdown = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  };

  // Fermeture différée — laisse le temps à la souris d'atteindre le menu enfant
  const scheduleCloseDropdown = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setDropdownOpen(false);
      closeTimerRef.current = null;
    }, 180);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 bg-[#0F3B5F] rounded-xl flex items-center justify-center">
            <i className="ri-home-gear-fill text-white text-xl"></i>
          </div>
          <div>
            <div className="font-bold text-xl text-[#0F3B5F] leading-none" style={{ fontFamily: "Barlow, sans-serif" }}>
              Up Diag
            </div>
            <div className="text-[10px] text-gray-500 leading-none mt-0.5">Diagnostic immobilier certifié</div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.dropdown ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={openDropdown}
                onMouseLeave={scheduleCloseDropdown}
              >
                <Link
                  to={link.href}
                  className={`text-sm font-medium flex items-center gap-1 transition-colors duration-200 py-2 ${
                    location.pathname.startsWith("/diagnostics")
                      ? "text-[#F97316]"
                      : "text-[#334155] hover:text-[#F97316]"
                  }`}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="menu"
                >
                  {link.label}
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </Link>
                {dropdownOpen && (
                  // pt-3 crée un "pont" invisible entre le bouton et la carte blanche :
                  // la souris traverse toujours un élément hoverable, pas de mouseleave parasite.
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-64 z-50"
                    onMouseEnter={openDropdown}
                    onMouseLeave={scheduleCloseDropdown}
                    role="menu"
                  >
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 py-2">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          role="menuitem"
                          className="block px-5 py-2.5 text-sm text-[#334155] hover:text-[#F97316] hover:bg-orange-50 transition-colors duration-150"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.href
                    ? "text-[#F97316]"
                    : "text-[#334155] hover:text-[#F97316]"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* CTA Desktop */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${PHONE.replace(/\s/g, "")}`}
            className="flex items-center gap-2 px-4 py-2.5 border-2 border-[#0F3B5F] text-[#0F3B5F] rounded-xl text-sm font-semibold hover:bg-[#0F3B5F] hover:text-white transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-phone-fill"></i>
            {PHONE}
          </a>
          <Link
            to="/devis"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#F97316] text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
          >
            <i className="ri-file-list-3-line"></i>
            Devis gratuit
          </Link>
        </div>

        {/* Burger Mobile */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-[#0F3B5F] cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <div key={link.label}>
                <Link
                  to={link.href}
                  className="block py-3 text-base font-medium text-[#334155] hover:text-[#F97316] border-b border-gray-50 transition-colors"
                >
                  {link.label}
                </Link>
                {link.dropdown && (
                  <div className="pl-4 flex flex-col gap-0.5 mt-1 mb-2">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block py-2 text-sm text-gray-500 hover:text-[#F97316] transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex flex-col gap-3 mt-4">
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                className="flex items-center justify-center gap-2 py-3 border-2 border-[#0F3B5F] text-[#0F3B5F] rounded-xl font-semibold whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                {PHONE}
              </a>
              <Link
                to="/devis"
                className="flex items-center justify-center gap-2 py-3 bg-[#F97316] text-white rounded-xl font-semibold whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                Devis gratuit
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
